/**
 * MobileScrollStrategy
 *
 * Mobile portrait strategy with progressive disclosure.
 * Shows 2 primary fields (Location, Date), tap to expand full modal.
 *
 * SOLID Compliance:
 * - SRP: Only renders mobile portrait layout
 * - OCP: Extends FilterUIStrategy (can be replaced without modification)
 * - LSP: Fully substitutable for FilterUIStrategy
 * - ISP: Implements only required strategy interface
 * - DIP: Depends on abstractions (URLStateManager, FilterRepository)
 *
 * Progressive Disclosure:
 * - Initial: Shows 2 fields (Location, Date) + "Search" button
 * - Expanded: Full modal with all primary filters + search
 *
 * Usage:
 * const strategy = new MobileScrollStrategy();
 * const ui = strategy.render(filters, urlStateManager, filterRepository);
 *
 * Location: apps/web/src/components/search/strategies/MobileScrollStrategy.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import React, { useState, useRef } from 'react';
import { Search, X } from 'lucide-react';
import FilterUIStrategy from './FilterUIStrategy';
import LocationAutocomplete from '../LocationAutocomplete';
import SearchDateRangePicker from '../../calendar/SearchDateRangePicker';
import GuestCountSelector from '../../calendar/GuestCountSelector';
import ActivityTypeSelector from '../ActivityTypeSelector';
import TagSelector from '../TagSelector';
import L2RefinementSection from '../L2RefinementSection';
import { getPrimaryFilters } from '../../../config/FilterRepository';
import useSearchFilters from '../../../hooks/useSearchFilters';
import useTaxonomy from '../../../hooks/useTaxonomy';

class MobileScrollStrategy extends FilterUIStrategy {
  constructor(options = {}) {
    super();
    this.fieldsVisible = options.fieldsVisible || 2; // Progressive disclosure: show 2 fields initially
  }

  getType() {
    return 'mobile-portrait';
  }

  supportsAdvancedFilters() {
    return false; // Mobile portrait doesn't show advanced filters
  }

  getVisiblePrimaryFilterCount() {
    return this.fieldsVisible;
  }

  render(filters, urlStateManager, filterRepository) {
    return (
      <MobileScrollStrategyComponent
        filters={filters}
        urlStateManager={urlStateManager}
        filterRepository={filterRepository}
        fieldsVisible={this.fieldsVisible}
      />
    );
  }
}

/**
 * MobileScrollStrategyComponent - React Component
 */
function MobileScrollStrategyComponent({ filters, urlStateManager, filterRepository: _filterRepository, fieldsVisible: _fieldsVisible }) {
  const [showModal, setShowModal] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const datePickerRef = useRef(null);
  const guestPickerRef = useRef(null);

  // Get primary filters from repository
  const _primaryFilters = getPrimaryFilters();

  // Use centralized search filters hook
  const {
    locationInput: location,
    localStartDate: startDate,
    localEndDate: endDate,
    localGuestCount: guestCount,
    localActivities,
    localTags,               // MVP 1.3 Phase 2
    handleLocationChange,
    handleLocationSelect,
    handleDateRangeSelect: hookHandleDateRangeSelect,
    handleGuestCountChange: hookHandleGuestCountChange,
    handleActivityChange: hookHandleActivityChange,
    handleTagToggle,         // MVP 1.3 Phase 2
    handleClearTags,         // MVP 1.3 Phase 2
    getDateDisplay,
    getGuestDisplay,
    formatDateForAPI,
  } = useSearchFilters(filters, urlStateManager);

  // Load taxonomy (MVP 1.3 Phase 2)
  const { taxonomy, loading: taxonomyLoading, error: _taxonomyError } = useTaxonomy();

  // Extract activity display values
  const activitySlug = localActivities.length > 0 ? localActivities[0].slug : '';
  const activityName = localActivities.length > 0 ? localActivities[0].name : '';

  /**
   * Wrapper for date range - closes picker and syncs to URL immediately
   */
  const handleDateRangeSelect = (start, end) => {
    hookHandleDateRangeSelect(start, end);
    // Immediately sync to URL for mobile
    urlStateManager.updateFilters({
      startDate: formatDateForAPI(start),
      endDate: formatDateForAPI(end)
    });
    setShowDatePicker(false);
  };

  /**
   * Wrapper for guest count - closes picker and syncs to URL immediately
   */
  const handleGuestCountChange = (count) => {
    hookHandleGuestCountChange(count);
    // Immediately sync to URL for mobile
    urlStateManager.updateFilter('partySize', count);
    setShowGuestPicker(false);
  };

  /**
   * Wrapper for activity type - syncs to URL immediately
   * Note: Hook expects array, but mobile uses single selection
   */
  const handleActivityChange = (slug, name) => {
    hookHandleActivityChange([{ slug, name }]);
    // Immediately sync to URL for mobile
    urlStateManager.updateFilters({
      activitySlug: slug,
      activityName: name
    });
  };

  /**
   * Handle L2 category toggle (MVP 1.3 Phase 2 - Progressive Search)
   */
  const handleL2Toggle = (slug) => {
    const currentL2 = filters.categoryL2 || '';
    const l2Slugs = currentL2 ? currentL2.split(',').map(s => s.trim()) : [];

    // Toggle: remove if present, add if not present
    const isPresent = l2Slugs.includes(slug);
    const newL2Slugs = isPresent
      ? l2Slugs.filter(s => s !== slug)
      : [...l2Slugs, slug];

    // Update URL immediately (mobile behavior)
    if (newL2Slugs.length > 0) {
      urlStateManager.updateFilter('categoryL2', newL2Slugs.join(','));
    } else {
      urlStateManager.clearFilter('categoryL2');
    }
  };

  /**
   * Execute search - filters already in URL, just close modal
   */
  const handleSearch = () => {
    // Filters already synced to URL via immediate updates
    // Just close the modal to show results
    setShowModal(false);
  };

  /**
   * Get display text for activity type
   */
  const _getActivityDisplay = () => {
    return activityName || activitySlug || 'Any type';
  };

  /**
   * Count active filters for display
   */
  const activeFiltersCount = urlStateManager.getActiveFilters().length;

  return (
    <>
      {/* Compact Search Button (Progressive Disclosure) */}
      <button
        onClick={() => setShowModal(true)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white border border-gray-300 rounded-full shadow-sm active:shadow-md transition"
      >
        <Search className="w-5 h-5 text-blue-600" />
        <div className="flex-1 text-left">
          <div className="text-sm font-medium text-gray-900">
            {location || 'What is your next adventure?'}
          </div>
          <div className="text-xs text-gray-500">
            {activeFiltersCount > 0
              ? `${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} selected`
              : 'Location • Dates • Guests'}
          </div>
        </div>
      </button>

      {/* Full Search Modal (Expanded) */}
      {showModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="p-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Search</h2>
                {activeFiltersCount > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search Fields */}
            <div className="space-y-4">
              {/* Location with Autocomplete */}
              <div className="border border-gray-300 rounded-lg p-4">
                <div className="text-xs font-semibold text-gray-900 mb-2">LOCATION</div>
                <LocationAutocomplete
                  value={location}
                  onChange={handleLocationChange}
                  onSelect={handleLocationSelect}
                  placeholder="Anywhere"
                  country="US"
                  debugId="MobileScrollStrategy"
                />
              </div>

              {/* Date Range Picker */}
              <div className="border border-gray-300 rounded-lg p-4">
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="w-full text-left"
                >
                  <div className="text-xs font-semibold text-gray-900 mb-1">DATE</div>
                  <div className={`${startDate && endDate ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                    {getDateDisplay()}
                  </div>
                </button>
                {showDatePicker && (
                  <div className="mt-4" ref={datePickerRef}>
                    <SearchDateRangePicker
                      onDateRangeSelect={handleDateRangeSelect}
                      onClose={() => setShowDatePicker(false)}
                    />
                  </div>
                )}
              </div>

              {/* Guests with Selector */}
              <div className="border border-gray-300 rounded-lg p-4">
                <button
                  onClick={() => setShowGuestPicker(!showGuestPicker)}
                  className="w-full text-left"
                >
                  <div className="text-xs font-semibold text-gray-900 mb-1">GUESTS</div>
                  <div className={`${guestCount > 1 ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                    {getGuestDisplay()}
                  </div>
                </button>
                {showGuestPicker && (
                  <div className="mt-4" ref={guestPickerRef}>
                    <GuestCountSelector
                      value={guestCount}
                      onChange={handleGuestCountChange}
                      onClose={() => setShowGuestPicker(false)}
                    />
                  </div>
                )}
              </div>

              {/* Adventure Type */}
              <div className="border border-gray-300 rounded-lg p-4">
                <div className="text-xs font-semibold text-gray-900 mb-3">ADVENTURE TYPE</div>
                <ActivityTypeSelector
                  value={{ slug: activitySlug, name: activityName }}
                  onChange={handleActivityChange}
                  variant="inline"
                />
              </div>

              {/* Tag Filters (MVP 1.3 Phase 2) */}
              {!taxonomyLoading && taxonomy && taxonomy.tags && taxonomy.tags.length > 0 && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-900 mb-3">TAGS</div>
                  <TagSelector
                    tags={taxonomy.tags || []}
                    selectedTags={localTags}
                    onTagToggle={handleTagToggle}
                    onClear={handleClearTags}
                    groupByCategory={true}
                  />
                </div>
              )}

              {/* L2 Category Refinement (MVP 1.3 Phase 2 - Progressive Search) */}
              {!taxonomyLoading && taxonomy && filters.categoryL1 && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <L2RefinementSection
                    selectedL1Slugs={filters.categoryL1}
                    selectedL2Slugs={filters.categoryL2}
                    onL2Toggle={handleL2Toggle}
                  />
                </div>
              )}
            </div>

            {/* Search Button */}
            <div className="mt-6">
              <button
                onClick={handleSearch}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition shadow-md flex items-center justify-center gap-2"
              >
                <Search className="w-5 h-5" />
                Search
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MobileScrollStrategy;
